import React, { useState } from 'react';
import { Form, Input, Button, Card, Alert, Space } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { authService } from '../services/authService';

const Login = ({ onLoginSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (values) => {
    setLoading(true);
    setError(null);

    const result = await authService.login(
      values.username,
      values.password
    );

    if (result.success) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      onLoginSuccess();
    } else {
      setError(result.error);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',

        backgroundColor: '#0c1a2b',
        backgroundImage: [
          'linear-gradient(rgba(148,178,209,0.05) 1px, transparent 1px)',
          'linear-gradient(90deg, rgba(148,178,209,0.05) 1px, transparent 1px)',
          'radial-gradient(ellipse at 50% 42%, #1d3450 0%, #152840 38%, #0e1f33 68%, #081426 100%)',
        ].join(', '),
        backgroundSize: '96px 96px, 96px 96px, 100% 100%',
      }}
    >
      {/* =========================================================
          TOP CORNER LABELS
      ========================================================== */}
      <style>{`
        .login-corner-label {
          position: absolute;
          top: 28px;
          color: rgba(190, 205, 224, 0.55);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          pointer-events: none;
          white-space: nowrap;
        }
        .login-corner-label--left {
          left: 32px;
        }
        .login-corner-label--right {
          right: 32px;
        }
        /* stack the labels on narrow screens so they can't overlap */
        @media (max-width: 768px) {
          .login-corner-label {
            font-size: 10px;
            letter-spacing: 0.18em;
          }
          .login-corner-label--left {
            left: 20px;
          }
          .login-corner-label--right {
            top: 48px;
            left: 20px;
            right: auto;
          }
        }
      `}</style>

      <div className="login-corner-label login-corner-label--left">
        Lincoln Reference Laboratory
      </div>

      <div className="login-corner-label login-corner-label--right">
        Secure Clinician Access
      </div>

      {/* =========================================================
          GEOMETRIC CONSTELLATION - LEFT SIDE
      ========================================================== */}
      <svg
        viewBox="0 0 640 760"
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          height: '100%',
          width: '620px',
          maxWidth: '46vw',
          opacity: 0.5,
          pointerEvents: 'none',
        }}
      >
        {/* rings */}
        <g stroke="rgba(174,190,210,0.22)" strokeWidth="1.5" fill="none">
          <circle cx="315" cy="185" r="78" />
          <circle cx="250" cy="315" r="105" />
          <circle cx="185" cy="305" r="42" />
          <circle cx="120" cy="130" r="26" />
        </g>
        <circle cx="318" cy="182" r="24" fill="rgba(174,190,210,0.20)" />

        {/* triangular mesh */}
        <g stroke="rgba(174,190,210,0.14)" strokeWidth="1" fill="none">
          <line x1="60" y1="90" x2="180" y2="160" />
          <line x1="180" y1="160" x2="120" y2="260" />
          <line x1="60" y1="90" x2="120" y2="260" />
          <line x1="180" y1="160" x2="318" y2="182" />
          <line x1="120" y1="260" x2="185" y2="305" />
          <line x1="185" y1="305" x2="318" y2="182" />
          <line x1="185" y1="305" x2="130" y2="430" />
          <line x1="130" y1="430" x2="250" y2="480" />
          <line x1="250" y1="480" x2="318" y2="382" />
          <line x1="318" y1="382" x2="185" y2="305" />
          <line x1="250" y1="480" x2="200" y2="600" />
          <line x1="200" y1="600" x2="80" y2="545" />
          <line x1="80" y1="545" x2="130" y2="430" />
          <line x1="318" y1="382" x2="430" y2="450" />
          <line x1="430" y1="450" x2="250" y2="480" />
          <line x1="60" y1="90" x2="40" y2="230" />
          <line x1="40" y1="230" x2="120" y2="260" />
        </g>

        {/* mesh nodes */}
        <g fill="rgba(190,205,224,0.35)">
          <circle cx="60" cy="90" r="3" />
          <circle cx="180" cy="160" r="3.5" />
          <circle cx="120" cy="260" r="3" />
          <circle cx="185" cy="305" r="3.5" />
          <circle cx="130" cy="430" r="3" />
          <circle cx="250" cy="480" r="3.5" />
          <circle cx="318" cy="382" r="3" />
          <circle cx="200" cy="600" r="3" />
          <circle cx="80" cy="545" r="3" />
          <circle cx="430" cy="450" r="3" />
          <circle cx="40" cy="230" r="3" />
        </g>
      </svg>

      {/* =========================================================
          DOTTED DNA HELIX - RIGHT SIDE
      ========================================================== */}
      <svg
        viewBox="0 0 260 900"
        preserveAspectRatio="xMidYMid meet"
        style={{
          position: 'absolute',
          right: '5vw',
          top: '50%',
          transform: 'translateY(-50%)',
          height: '92vh',
          width: '280px',
          opacity: 0.55,
          pointerEvents: 'none',
        }}
      >
        {/* rungs: thin line with a dot at each strand */}
        <g
          stroke="rgba(174,190,210,0.28)"
          strokeWidth="1"
          fill="rgba(190,205,224,0.5)"
        >
          <line x1="101" y1="20" x2="159" y2="20" />
          <circle cx="101" cy="20" r="3.5" />
          <circle cx="159" cy="20" r="3.5" />

          <line x1="65" y1="50" x2="195" y2="50" />
          <circle cx="65" cy="50" r="3.5" />
          <circle cx="195" cy="50" r="3.5" />

          <line x1="46" y1="80" x2="214" y2="80" />
          <circle cx="46" cy="80" r="3.5" />
          <circle cx="214" cy="80" r="3.5" />

          <line x1="50" y1="110" x2="210" y2="110" />
          <circle cx="50" cy="110" r="3.5" />
          <circle cx="210" cy="110" r="3.5" />

          <line x1="75" y1="140" x2="185" y2="140" />
          <circle cx="75" cy="140" r="3.5" />
          <circle cx="185" cy="140" r="3.5" />

          <circle cx="115" cy="170" r="3.5" />
          <circle cx="145" cy="170" r="3.5" />

          <line x1="101" y1="200" x2="159" y2="200" />
          <circle cx="101" cy="200" r="3.5" />
          <circle cx="159" cy="200" r="3.5" />

          <line x1="65" y1="230" x2="195" y2="230" />
          <circle cx="65" cy="230" r="3.5" />
          <circle cx="195" cy="230" r="3.5" />

          <line x1="46" y1="260" x2="214" y2="260" />
          <circle cx="46" cy="260" r="3.5" />
          <circle cx="214" cy="260" r="3.5" />

          <line x1="50" y1="290" x2="210" y2="290" />
          <circle cx="50" cy="290" r="3.5" />
          <circle cx="210" cy="290" r="3.5" />

          <line x1="75" y1="320" x2="185" y2="320" />
          <circle cx="75" cy="320" r="3.5" />
          <circle cx="185" cy="320" r="3.5" />

          <circle cx="115" cy="350" r="3.5" />
          <circle cx="145" cy="350" r="3.5" />

          <line x1="101" y1="380" x2="159" y2="380" />
          <circle cx="101" cy="380" r="3.5" />
          <circle cx="159" cy="380" r="3.5" />

          <line x1="65" y1="410" x2="195" y2="410" />
          <circle cx="65" cy="410" r="3.5" />
          <circle cx="195" cy="410" r="3.5" />

          <line x1="46" y1="440" x2="214" y2="440" />
          <circle cx="46" cy="440" r="3.5" />
          <circle cx="214" cy="440" r="3.5" />

          <line x1="50" y1="470" x2="210" y2="470" />
          <circle cx="50" cy="470" r="3.5" />
          <circle cx="210" cy="470" r="3.5" />

          <line x1="75" y1="500" x2="185" y2="500" />
          <circle cx="75" cy="500" r="3.5" />
          <circle cx="185" cy="500" r="3.5" />

          <circle cx="115" cy="530" r="3.5" />
          <circle cx="145" cy="530" r="3.5" />

          <line x1="101" y1="560" x2="159" y2="560" />
          <circle cx="101" cy="560" r="3.5" />
          <circle cx="159" cy="560" r="3.5" />

          <line x1="65" y1="590" x2="195" y2="590" />
          <circle cx="65" cy="590" r="3.5" />
          <circle cx="195" cy="590" r="3.5" />

          <line x1="46" y1="620" x2="214" y2="620" />
          <circle cx="46" cy="620" r="3.5" />
          <circle cx="214" cy="620" r="3.5" />

          <line x1="50" y1="650" x2="210" y2="650" />
          <circle cx="50" cy="650" r="3.5" />
          <circle cx="210" cy="650" r="3.5" />

          <line x1="75" y1="680" x2="185" y2="680" />
          <circle cx="75" cy="680" r="3.5" />
          <circle cx="185" cy="680" r="3.5" />

          <circle cx="115" cy="710" r="3.5" />
          <circle cx="145" cy="710" r="3.5" />

          <line x1="101" y1="740" x2="159" y2="740" />
          <circle cx="101" cy="740" r="3.5" />
          <circle cx="159" cy="740" r="3.5" />

          <line x1="65" y1="770" x2="195" y2="770" />
          <circle cx="65" cy="770" r="3.5" />
          <circle cx="195" cy="770" r="3.5" />

          <line x1="46" y1="800" x2="214" y2="800" />
          <circle cx="46" cy="800" r="3.5" />
          <circle cx="214" cy="800" r="3.5" />

          <line x1="50" y1="830" x2="210" y2="830" />
          <circle cx="50" cy="830" r="3.5" />
          <circle cx="210" cy="830" r="3.5" />

          <line x1="75" y1="860" x2="185" y2="860" />
          <circle cx="75" cy="860" r="3.5" />
          <circle cx="185" cy="860" r="3.5" />

          <circle cx="115" cy="890" r="3.5" />
          <circle cx="145" cy="890" r="3.5" />
        </g>
      </svg>

      {/* =========================================================
          SCATTERED DOTS - FULL BACKDROP
      ========================================================== */}
      <svg
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <g fill="rgba(190,205,224,0.28)">
          <circle cx="120" cy="620" r="3" />
          <circle cx="260" cy="90" r="2.5" />
          <circle cx="370" cy="560" r="3.5" />
          <circle cx="90" cy="820" r="2.5" />
          <circle cx="440" cy="760" r="2.5" />
          <circle cx="520" cy="200" r="3" />
          <circle cx="300" cy="720" r="2" />
          <circle cx="560" cy="640" r="2" />
          <circle cx="180" cy="440" r="2" />
          <circle cx="1180" cy="120" r="3" />
          <circle cx="1300" cy="300" r="2.5" />
          <circle cx="1120" cy="520" r="2" />
          <circle cx="1480" cy="620" r="3" />
          <circle cx="1380" cy="80" r="2" />
          <circle cx="1240" cy="780" r="2.5" />
          <circle cx="1520" cy="240" r="2" />
          <circle cx="1100" cy="840" r="2" />
          <circle cx="800" cy="60" r="2" />
          <circle cx="700" cy="860" r="2" />
        </g>
      </svg>

      {/* =========================================================
          LOGIN CARD
      ========================================================== */}

      <Card
        style={{
          width: 400,
          position: 'relative',
          zIndex: 2,

          borderRadius: '8px',
          border: '1px solid rgba(255,255,255,0.2)',

          background: '#ffffff',

          boxShadow:
            '0 18px 45px rgba(8, 19, 35, 0.32)',
        }}
        styles={{
          body: {
            padding: '28px 26px 22px',
          },
        }}
      >
        <Space
          direction="vertical"
          size="large"
          style={{
            width: '100%',
          }}
        >
          {/* Header */}

          <div
            style={{
              textAlign: 'center',
              marginBottom: '24px',
            }}
          >
            <h1
              style={{
                fontSize: '38px',
                fontWeight: 600,
                color: '#1e3a5c',
                margin: '0 0 2px 0',
              }}
            >
              ConnectMD
            </h1>

            <p
              style={{
                fontSize: '14px',
                color: '#8c8c8c',
                margin: 0,
              }}
            >
              by Lincoln Reference Laboratory
            </p>
          </div>

          {/* Login error */}

          {error && (
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
            />
          )}

          {/* Form */}

          <Form
            name="login"
            onFinish={handleSubmit}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              label="Username"
              name="username"
              rules={[
                {
                  required: true,
                  message: 'Please input your username!',
                },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="Username"
                size="large"
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Please input your password!',
                },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Password"
                size="large"
              />
            </Form.Item>

            <Form.Item
              style={{
                marginBottom: 0,
                marginTop: '6px',
              }}
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                style={{
                  background: '#5b82a6',
                  borderColor: '#5b82a6',
                }}
              >
                Log in
              </Button>
            </Form.Item>
          </Form>
        </Space>
      </Card>
    </div>
  );
};

export default Login;